/**
 * ConditionField - Visual condition builder
 *
 * Supports AND/OR grouping with nested conditions.
 * Operands support expression tokens via TokenizedInput.
 * Serializes to Azure Logic Apps expression format.
 */

import { useState, useCallback, useMemo, useRef, useEffect, type MutableRefObject, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Label,
  Text,
  Button,
  Dropdown,
  Option,
  Tooltip,
} from '@fluentui/react-components';
import {
  AddFilled,
  DeleteFilled,
  CodeFilled,
  TextFieldFilled,
  BranchForkFilled,
} from '@fluentui/react-icons';
import { TokenizedInput } from './TokenizedInput';
import { ExpressionPreview } from './ExpressionPreview';
import { EndActionsSlot } from '../shared/EndActionsSlot';
import { useFieldPreviewFocus } from './useFieldPreviewFocus';
import type { AgentParameterSchema } from './AgentParameterMenu';
import type { ValueSegment } from '@/types/valueSegment';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';

import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  modeToggle: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
  },
  // Root group styling
  rootGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.borderLeft('3px', 'solid', tokens.colorBrandStroke1),
    position: 'relative',
  },
  // Nested group styling (slightly different color to distinguish nesting)
  nestedGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.borderLeft('3px', 'solid', tokens.colorBrandStroke2),
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  operatorSelector: {
    minWidth: '80px',
    // Match height of TokenizedInput (32px)
    '& button': {
      height: '32px',
      minHeight: '32px',
    },
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr auto',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center', // Changed from flex-start to center for alignment
  },
  rowUnary: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  // Simplified single-row layout used by isOldFormat mode (no AND/OR group, no delete column)
  simpleRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  simpleRowUnary: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  operandInput: {
    flex: 1,
  },
  comparisonSelector: {
    minWidth: '140px',
    // Match height of TokenizedInput (32px)
    '& button': {
      height: '32px',
      minHeight: '32px',
    },
  },
  deleteButton: {
    minWidth: 'auto',
    height: '32px',
    padding: tokens.spacingHorizontalS,
  },
  addButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    fontStyle: 'italic',
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Wrapper for visual editor + preview
  editorWrapper: {
    position: 'relative',
  },
});

// Condition operators - internal names
export type ConditionOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'greaterThan'
  | 'lessThan'
  | 'greaterOrEquals'
  | 'lessOrEquals'
  | 'isEmpty'
  | 'isNotEmpty';

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  notEquals: 'not equals',
  contains: 'contains',
  notContains: 'does not contain',
  startsWith: 'starts with',
  endsWith: 'ends with',
  greaterThan: 'greater than',
  lessThan: 'less than',
  greaterOrEquals: 'greater or equal',
  lessOrEquals: 'less or equal',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
};

// Operators that don't need a second operand
const UNARY_OPERATORS: ConditionOperator[] = ['isEmpty', 'isNotEmpty'];

interface ConditionRow {
  id: string;
  type: 'row';
  operand1: ValueSegment[];
  operator: ConditionOperator;
  operand2: ValueSegment[];
}

interface ConditionGroup {
  id: string;
  type: 'group';
  operator: 'and' | 'or' | 'not';
  items: (ConditionRow | ConditionGroup)[];
}

type ConditionItem = ConditionRow | ConditionGroup;

// Separate interfaces for the different condition types
interface SerializedAndCondition {
  and: SerializedCondition[];
}

interface SerializedOrCondition {
  or: SerializedCondition[];
}

interface SerializedNotCondition {
  not: SerializedCondition;
}

interface SerializedRowCondition {
  [operator: string]: (string | number | boolean)[];
}

type SerializedCondition = SerializedAndCondition | SerializedOrCondition | SerializedNotCondition | SerializedRowCondition;

const INTERNAL_TO_SERIALIZED_OPERATOR_MAP: Record<ConditionOperator, string> = {
  equals: 'equals',
  notEquals: 'notEquals',
  contains: 'contains',
  notContains: 'notContains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  greaterThan: 'greater',
  lessThan: 'less',
  greaterOrEquals: 'greaterOrEquals',
  lessOrEquals: 'lessOrEquals',
  isEmpty: 'isEmpty',
  isNotEmpty: 'isNotEmpty',
};

const SERIALIZED_TO_INTERNAL_OPERATOR_MAP: Record<string, ConditionOperator> = {
  equals: 'equals',
  notEquals: 'notEquals',
  not_equals: 'notEquals',
  contains: 'contains',
  notContains: 'notContains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  greater: 'greaterThan',
  greaterThan: 'greaterThan',
  less: 'lessThan',
  lessThan: 'lessThan',
  greaterOrEquals: 'greaterOrEquals',
  greaterThanOrEquals: 'greaterOrEquals',
  lessOrEquals: 'lessOrEquals',
  lessThanOrEquals: 'lessOrEquals',
  isEmpty: 'isEmpty',
  isNotEmpty: 'isNotEmpty',
};

export function getSerializedConditionOperator(operator: ConditionOperator): string {
  return INTERNAL_TO_SERIALIZED_OPERATOR_MAP[operator] ?? operator;
}

export function getInternalConditionOperator(operator: string): ConditionOperator {
  return SERIALIZED_TO_INTERNAL_OPERATOR_MAP[operator] ?? 'equals';
}

export interface ConditionFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Available operators (defaults to all) */
  operators?: ConditionOperator[] | undefined;
  /** Show the agent parameter button */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs for previews */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since last run */
  isStale?: boolean | undefined;
  /** Serialize to old-format string expression (e.g., @equals(x, y)) instead of JSON object.
   *  Used by Until and Query actions. */
  isOldFormat?: boolean | undefined;
  /**
   * Optional slot rendered in the label row alongside the visual / JSON
   * mode toggle. Hosts inline affordances such as `FieldActionChips` so
   * they share the same right-aligned area as the toggle instead of
   * floating absolutely on top of it. Same contract as StringField etc.
   */
  endActions?: ReactNode | undefined;
}

/**
 * Check if a value is a raw workflow expression (e.g., @greater(x, y), ${...}).
 * Filter/Query 'where' clauses can be bare expressions that aren't parseable as JSON conditions.
 */
function getConditionExpressionString(value: string | ValueSegment[]): string | null {
  const str = isValueSegmentArray(value) ? segmentsToString(value) : value;
  if (!str) return null;
  const trimmed = str.trim();
  if (trimmed.startsWith('@') || trimmed.startsWith('${')) {
    return trimmed;
  }
  return null;
}

/**
 * Split arguments of a function call, respecting nested parentheses and quoted strings.
 * E.g., "variables('counter'), 10" → ["variables('counter')", "10"]
 */
function splitFunctionArgs(argsStr: string): string[] {
  const args: string[] = [];
  let depth = 0;
  let inSingleQuote = false;
  let current = '';

  for (let i = 0; i < argsStr.length; i++) {
    const ch = argsStr[i]!;
    if (inSingleQuote) {
      current += ch;
      // Escaped quote ''
      if (ch === "'" && i + 1 < argsStr.length && argsStr[i + 1] === "'") {
        current += argsStr[i + 1];
        i++;
      } else if (ch === "'") {
        inSingleQuote = false;
      }
      continue;
    }
    if (ch === "'") {
      inSingleQuote = true;
      current += ch;
    } else if (ch === '(') {
      depth++;
      current += ch;
    } else if (ch === ')') {
      depth--;
      current += ch;
    } else if (ch === ',' && depth === 0) {
      args.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) {
    args.push(current.trim());
  }
  return args;
}

/**
 * Reverse map from Logic Apps expression function names back to internal operators.
 */
const REVERSE_OLD_FORMAT_MAP: Record<string, ConditionOperator> = {
  equals: 'equals',
  contains: 'contains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  greater: 'greaterThan',
  less: 'lessThan',
  greaterOrEquals: 'greaterOrEquals',
  lessOrEquals: 'lessOrEquals',
  empty: 'isEmpty',
};

/**
 * Format an operand back to display format for the visual builder.
 * Expression function calls get wrapped in ${...}, quoted strings get unquoted.
 */
function unformatOldFormatOperand(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  // Quoted string literal — unwrap quotes
  if (trimmed.startsWith("'") && trimmed.endsWith("'") && trimmed.length >= 2) {
    return trimmed.slice(1, -1).replace(/''/g, "'");
  }
  // Boolean / null / numeric — keep as-is
  if (trimmed === 'true' || trimmed === 'false' || trimmed === 'null' || !isNaN(Number(trimmed))) {
    return trimmed;
  }
  // Expression function call — wrap in ${...}
  return `\${${trimmed}}`;
}

/**
 * Parse a single old-format expression (without leading @) into a ConditionItem.
 * E.g., "equals(variables('x'), 10)" → ConditionRow
 *        "and(equals(a, b), greater(c, d))" → ConditionGroup
 *        "not(equals(a, b))" → ConditionGroup with operator 'not'
 */
function parseOldFormatExpr(expr: string): ConditionItem {
  const trimmed = expr.trim();

  // Find function name and opening paren
  const parenIdx = trimmed.indexOf('(');
  if (parenIdx === -1) {
    // No function call — treat as a single-operand condition
    return {
      ...createConditionRow(),
      operand1: [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: trimmed }],
    };
  }

  const funcName = trimmed.slice(0, parenIdx).trim();
  // Extract content between outer parens
  const inner = trimmed.slice(parenIdx + 1, trimmed.lastIndexOf(')')).trim();

  // Group operators
  if (funcName === 'and' || funcName === 'or') {
    const childExprs = splitFunctionArgs(inner);
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: funcName,
      items: childExprs.map((child) => parseOldFormatExpr(child)),
    };
  }

  if (funcName === 'not') {
    const childItem = parseOldFormatExpr(inner);
    // Check if the inner is a negated operator (not(equals(...)) → notEquals, not(contains(...)) → notContains)
    if (childItem.type === 'row') {
      if (childItem.operator === 'equals') {
        return { ...childItem, operator: 'notEquals' };
      }
      if (childItem.operator === 'contains') {
        return { ...childItem, operator: 'notContains' };
      }
      if (childItem.operator === 'isEmpty') {
        return { ...childItem, operator: 'isNotEmpty' };
      }
    }
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'not',
      items: [childItem],
    };
  }

  // Row operators (equals, greater, etc.)
  const operator = REVERSE_OLD_FORMAT_MAP[funcName];
  if (operator) {
    const args = splitFunctionArgs(inner);
    const op1 = unformatOldFormatOperand(args[0] || '');
    const op2 = unformatOldFormatOperand(args[1] || '');
    return {
      id: generateSegmentId(),
      type: 'row',
      operand1: [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: op1 }],
      operator,
      operand2: [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: op2 }],
    };
  }

  // Unknown function — put the whole expression as operand1
  return {
    ...createConditionRow(),
    operand1: [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: `\${${trimmed}}` }],
  };
}

// ---------------------------------------------------------------------------
// JS-form condition parser
//
// Recognises the JS shape that `serializeConditionOldFormat` emits, e.g.
//   triggerBody()?.['x'] == 'foo'
//   (a == 'b' && c > 10)
//   !(x.startsWith('y'))
//   (x == null || x == '')                // folded to isEmpty
// Returns null when the input isn't recognisable as a JS condition; callers
// fall back to the legacy function-call parser.
// ---------------------------------------------------------------------------

function stripOuterParens(s: string): string {
  let cur = s.trim();
  while (cur.startsWith('(') && cur.endsWith(')') && findMatchingClose(cur, 0) === cur.length - 1) {
    cur = cur.slice(1, -1).trim();
  }
  return cur;
}

function splitTopLevel(s: string, sep: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  let start = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (inSingle) {
      if (ch === '\\' && i + 1 < s.length) { i++; continue; }
      if (ch === "'") {
        if (s[i + 1] === "'") { i++; continue; }
        inSingle = false;
      }
      continue;
    }
    if (inDouble) {
      if (ch === '\\' && i + 1 < s.length) { i++; continue; }
      if (ch === '"') inDouble = false;
      continue;
    }
    if (ch === "'") { inSingle = true; continue; }
    if (ch === '"') { inDouble = true; continue; }
    if (ch === '(' || ch === '[') { depth++; continue; }
    if (ch === ')' || ch === ']') { depth--; continue; }
    if (depth === 0 && s.startsWith(sep, i)) {
      parts.push(s.slice(start, i));
      start = i + sep.length;
      i += sep.length - 1;
    }
  }
  parts.push(s.slice(start));
  return parts.map((p) => p.trim());
}

function findTopLevelOperator(s: string, ops: readonly string[]): { op: string; index: number } | null {
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (inSingle) {
      if (ch === '\\' && i + 1 < s.length) { i++; continue; }
      if (ch === "'") {
        if (s[i + 1] === "'") { i++; continue; }
        inSingle = false;
      }
      continue;
    }
    if (inDouble) {
      if (ch === '\\' && i + 1 < s.length) { i++; continue; }
      if (ch === '"') inDouble = false;
      continue;
    }
    if (ch === "'") { inSingle = true; continue; }
    if (ch === '"') { inDouble = true; continue; }
    if (ch === '(' || ch === '[') { depth++; continue; }
    if (ch === ')' || ch === ']') { depth--; continue; }
    if (depth === 0) {
      for (const op of ops) {
        if (s.startsWith(op, i)) return { op, index: i };
      }
    }
  }
  return null;
}

function unquoteJsString(s: string): string | null {
  const t = s.trim();
  if (t.length < 2) return null;
  const q = t[0];
  if ((q !== "'" && q !== '"') || t[t.length - 1] !== q) return null;
  let out = '';
  for (let i = 1; i < t.length - 1; i++) {
    const ch = t[i]!;
    if (ch === '\\' && i + 1 < t.length - 1) {
      const next = t[i + 1]!;
      out += next === 'n' ? '\n' : next === 't' ? '\t' : next === 'r' ? '\r' : next;
      i++;
      continue;
    }
    if (q === "'" && ch === "'" && t[i + 1] === "'") { out += "'"; i++; continue; }
    out += ch;
  }
  return out;
}

function unformatJsOperand(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const unquoted = unquoteJsString(trimmed);
  if (unquoted !== null) return unquoted;
  if (trimmed === 'true' || trimmed === 'false' || trimmed === 'null' || !isNaN(Number(trimmed))) {
    return trimmed;
  }
  return `\${${trimmed}}`;
}

const JS_INFIX_TO_OPERATOR: Record<string, ConditionOperator> = {
  '==': 'equals',
  '===': 'equals',
  '!=': 'notEquals',
  '!==': 'notEquals',
  '>': 'greaterThan',
  '<': 'lessThan',
  '>=': 'greaterOrEquals',
  '<=': 'lessOrEquals',
};

function rowFromInfix(operator: ConditionOperator, leftRaw: string, rightRaw: string): ConditionRow {
  return {
    id: generateSegmentId(),
    type: 'row',
    operand1: [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: unformatJsOperand(leftRaw) }],
    operator,
    operand2: [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: unformatJsOperand(rightRaw) }],
  };
}

function rowFromUnary(operator: ConditionOperator, operandRaw: string): ConditionRow {
  return {
    id: generateSegmentId(),
    type: 'row',
    operand1: [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: unformatJsOperand(operandRaw) }],
    operator,
    operand2: [{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: '' }],
  };
}

function matchMethodCall(s: string, method: string): { receiver: string; arg: string } | null {
  const suffix = `.${method}(`;
  if (!s.endsWith(')')) return null;
  let depth = 0;
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (inSingle) {
      if (ch === '\\' && i + 1 < s.length) { i++; continue; }
      if (ch === "'") {
        if (s[i + 1] === "'") { i++; continue; }
        inSingle = false;
      }
      continue;
    }
    if (inDouble) {
      if (ch === '\\' && i + 1 < s.length) { i++; continue; }
      if (ch === '"') inDouble = false;
      continue;
    }
    if (ch === "'") { inSingle = true; continue; }
    if (ch === '"') { inDouble = true; continue; }
    if (ch === '(' || ch === '[') { depth++; continue; }
    if (ch === ')' || ch === ']') { depth--; continue; }
    if (depth === 0 && s.startsWith(suffix, i)) {
      const openParen = i + suffix.length - 1;
      const close = findMatchingClose(s, openParen);
      if (close !== s.length - 1) return null;
      // Strip outer parens from the receiver so round-tripping
      // `(receiver).includes(arg)` (serializer always parenthesises) doesn't
      // accumulate `((receiver)).includes(arg)` on each save.
      return {
        receiver: stripOuterParens(s.slice(0, i)),
        arg: s.slice(openParen + 1, close).trim(),
      };
    }
  }
  return null;
}

function matchEmptyFold(parts: string[], op: 'isEmpty' | 'isNotEmpty'): ConditionRow | null {
  if (parts.length !== 2) return null;
  const cmpOp = op === 'isEmpty' ? '==' : '!=';
  const nullLit = 'null';
  const emptyLit = "''";
  const findOperand = (part: string, rhs: string): string | null => {
    const stripped = stripOuterParens(part);
    const hit = findTopLevelOperator(stripped, [cmpOp]);
    if (!hit) return null;
    const left = stripped.slice(0, hit.index).trim();
    const right = stripped.slice(hit.index + cmpOp.length).trim();
    return right === rhs ? left : null;
  };
  const a = findOperand(parts[0]!, nullLit);
  const b = a ? findOperand(parts[1]!, emptyLit) : null;
  const altA = !a ? findOperand(parts[0]!, emptyLit) : null;
  const altB = altA ? findOperand(parts[1]!, nullLit) : null;
  const left = a && b && a === b ? a : (altA && altB && altA === altB ? altA : null);
  if (!left) return null;
  return rowFromUnary(op, left);
}

function parseJsExpr(s: string): ConditionItem | null {
  const stripped = stripOuterParens(s);
  if (!stripped) return null;

  // OR group / isEmpty fold
  const orParts = splitTopLevel(stripped, '||');
  if (orParts.length > 1) {
    const empty = matchEmptyFold(orParts, 'isEmpty');
    if (empty) return empty;
    const items = orParts.map(parseJsExpr);
    if (items.some((it) => it == null)) return null;
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'or',
      items: items as ConditionItem[],
    };
  }

  // AND group / isNotEmpty fold
  const andParts = splitTopLevel(stripped, '&&');
  if (andParts.length > 1) {
    const notEmpty = matchEmptyFold(andParts, 'isNotEmpty');
    if (notEmpty) return notEmpty;
    const items = andParts.map(parseJsExpr);
    if (items.some((it) => it == null)) return null;
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'and',
      items: items as ConditionItem[],
    };
  }

  // NOT (leading `!`, but not `!=`)
  if (stripped.startsWith('!') && stripped[1] !== '=') {
    const innerItem = parseJsExpr(stripped.slice(1));
    if (!innerItem) return null;
    // Fold !(x.includes(y)) → notContains row
    if (innerItem.type === 'row' && innerItem.operator === 'contains') {
      return { ...innerItem, operator: 'notContains' };
    }
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'not',
      items: [innerItem],
    };
  }

  // Method-call shapes
  for (const method of ['includes', 'startsWith', 'endsWith'] as const) {
    const m = matchMethodCall(stripped, method);
    if (m) {
      const op: ConditionOperator = method === 'includes' ? 'contains' : method;
      return rowFromInfix(op, m.receiver, m.arg);
    }
  }

  // Comparison operators in precedence order (longest first per bucket).
  for (const ops of [['==='], ['!=='], ['=='], ['!='], ['<=', '>='], ['<', '>']]) {
    const hit = findTopLevelOperator(stripped, ops);
    if (hit) {
      const op = JS_INFIX_TO_OPERATOR[hit.op];
      if (!op) continue;
      const left = stripped.slice(0, hit.index);
      const right = stripped.slice(hit.index + hit.op.length);
      if (!left.trim() || !right.trim()) return null;
      return rowFromInfix(op, left, right);
    }
  }

  return null;
}

/**
 * Parse an old-format expression string (e.g., "${equals(x, y)}" or
 * "${triggerBody()?.['x'] == 'foo'}") into a ConditionGroup. Tries the
 * JS-infix form first (current serialization output); falls back to the
 * legacy function-call form for round-tripping older saved workflows.
 */
function parseOldFormatCondition(expression: string): ConditionGroup {
  const inner = unwrapOldFormatExpression(expression) ?? expression.trim();
  if (!inner) return createEmptyGroup();

  const item = parseJsExpr(inner) ?? parseOldFormatExpr(inner);
  if (item.type === 'group') {
    return item;
  }
  return {
    id: generateSegmentId(),
    type: 'group',
    operator: 'and',
    items: [item],
  };
}

/**
 * Parse serialized condition format into internal ConditionGroup
 */
function parseCondition(value: string | ValueSegment[]): ConditionGroup {
  const stringValue = isValueSegmentArray(value)
    ? segmentsToString(value)
    : value;

  if (!stringValue || stringValue.trim() === '') {
    return createEmptyGroup();
  }

  // Old-format expression (e.g., @equals(x, y)) — parse into condition group
  const trimmed = stringValue.trim();
  if (trimmed.startsWith('@') || trimmed.startsWith('${')) {
    return parseOldFormatCondition(trimmed);
  }

  try {
    const parsed = JSON.parse(stringValue);

    // Check for direct format: { and/or/not: [...] }
    if (parsed && ('and' in parsed || 'or' in parsed || 'not' in parsed)) {
      return parseSerializedCondition(parsed as SerializedCondition);
    }

    // Legacy format with expression wrapper: { expression: { and/or: [...] } }
    if (parsed && parsed.expression) {
      return parseSerializedCondition(parsed.expression as SerializedCondition);
    }

    // Legacy internal format: { type: 'group', ... }
    if (parsed && parsed.type === 'group') {
      return parsed as ConditionGroup;
    }
  } catch {
    // Not valid JSON - create empty group
  }

  return createEmptyGroup();
}

/**
 * Type guard for and condition
 */
function isAndCondition(condition: SerializedCondition): condition is SerializedAndCondition {
  return 'and' in condition && Array.isArray((condition as SerializedAndCondition).and);
}

/**
 * Type guard for or condition
 */
function isOrCondition(condition: SerializedCondition): condition is SerializedOrCondition {
  return 'or' in condition && Array.isArray((condition as SerializedOrCondition).or);
}

/**
 * Type guard for not condition
 */
function isNotCondition(condition: SerializedCondition): condition is SerializedNotCondition {
  return 'not' in condition && (condition as SerializedNotCondition).not !== undefined;
}

/**
 * Parse serialized condition format into internal format
 */
function parseSerializedCondition(condition: SerializedCondition): ConditionGroup {
  // Check if it's a group (and/or/not)
  if (isAndCondition(condition)) {
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'and',
      items: condition.and.map((item) => parseSerializedConditionItem(item)),
    };
  }

  if (isOrCondition(condition)) {
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'or',
      items: condition.or.map((item) => parseSerializedConditionItem(item)),
    };
  }

  if (isNotCondition(condition)) {
    const innerItem = parseSerializedConditionItem(condition.not);
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'not',
      items: [innerItem],
    };
  }

  // It's a row - find the operator key
  const operatorKey = Object.keys(condition)[0];
  if (!operatorKey) {
    return createEmptyGroup();
  }

  const operands = (condition as SerializedRowCondition)[operatorKey] || [];
  const operand1 = String(operands[0] ?? '');
  const operand2 = String(operands[1] ?? '');

  // Map serialized operator to internal operator
  const operator = getInternalConditionOperator(operatorKey);

  return {
    id: generateSegmentId(),
    type: 'group',
    operator: 'and',
    items: [{
      id: generateSegmentId(),
      type: 'row',
      operand1: [{
        id: generateSegmentId(),
        type: ValueSegmentType.LITERAL,
        value: operand1,
      }],
      operator,
      operand2: [{
        id: generateSegmentId(),
        type: ValueSegmentType.LITERAL,
        value: operand2,
      }],
    }],
  };
}

/**
 * Parse a single serialized condition item (returns ConditionRow or ConditionGroup)
 */
function parseSerializedConditionItem(condition: SerializedCondition): ConditionItem {
  // Check if it's a group (and/or/not)
  if (isAndCondition(condition)) {
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'and',
      items: condition.and.map((item) => parseSerializedConditionItem(item)),
    };
  }

  if (isOrCondition(condition)) {
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'or',
      items: condition.or.map((item) => parseSerializedConditionItem(item)),
    };
  }

  if (isNotCondition(condition)) {
    const innerItem = parseSerializedConditionItem(condition.not);
    return {
      id: generateSegmentId(),
      type: 'group',
      operator: 'not',
      items: [innerItem],
    };
  }

  // It's a row - find the operator key
  const operatorKey = Object.keys(condition)[0];
  if (!operatorKey) {
    // Return empty row as fallback
    return createConditionRow();
  }

  const operands = (condition as SerializedRowCondition)[operatorKey] || [];
  const operand1 = String(operands[0] ?? '');
  const operand2 = String(operands[1] ?? '');

  // Map serialized operator to internal operator
  const operator = getInternalConditionOperator(operatorKey);

  return {
    id: generateSegmentId(),
    type: 'row',
    operand1: [{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: operand1,
    }],
    operator,
    operand2: [{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: operand2,
    }],
  };
}

/**
 * Create a starter condition group containing one blank row, so users can
 * begin editing immediately without first pressing "Add Condition".
 */
function createEmptyGroup(): ConditionGroup {
  return {
    id: generateSegmentId(),
    type: 'group',
    operator: 'and',
    items: [createConditionRow()],
  };
}

/**
 * Create a new condition row
 */
function createConditionRow(): ConditionRow {
  return {
    id: generateSegmentId(),
    type: 'row',
    operand1: [{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: '',
    }],
    operator: 'equals',
    operand2: [{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: '',
    }],
  };
}

/**
 * Create a new nested group
 */
function createNestedGroup(): ConditionGroup {
  return {
    id: generateSegmentId(),
    type: 'group',
    operator: 'and',
    items: [createConditionRow()],
  };
}

/**
 * Coerce a string operand to its native type for serialization.
 * Preserves numbers and booleans so the workflow JSON matches the runtime's
 * expected format (e.g., `"greater": ["${expr}", 100]` instead of `"100"`).
 */
function coerceOperandType(value: string): string | number | boolean {
  // Don't coerce expressions — they must remain strings
  if (value.startsWith('${') || value.startsWith('@')) {
    return value;
  }

  // Boolean literals
  if (value === 'true') return true;
  if (value === 'false') return false;

  // Numeric literals (integers and decimals, but not empty string or whitespace-only)
  if (value !== '' && !isNaN(Number(value)) && value.trim() === value) {
    return Number(value);
  }

  return value;
}

/**
 * Serialize a condition row to expression format
 */
function serializeRow(row: ConditionRow): SerializedCondition {
  const operand1 = segmentsToString(row.operand1);
  const operand2 = segmentsToString(row.operand2);
  const operator = getSerializedConditionOperator(row.operator);

  // Unary operators only need one operand
  if (UNARY_OPERATORS.includes(row.operator)) {
    return { [operator]: [coerceOperandType(operand1)] } as SerializedCondition;
  }

  return { [operator]: [coerceOperandType(operand1), coerceOperandType(operand2)] } as SerializedCondition;
}

/**
 * Serialize a condition group to expression format
 */
function serializeGroup(group: ConditionGroup): SerializedCondition {
  const serializedItems = group.items.map((item) =>
    item.type === 'row' ? serializeRow(item) : serializeGroup(item)
  );

  if (group.operator === 'and') {
    return { and: serializedItems };
  } else if (group.operator === 'not') {
    // NOT wraps a single child condition. If multiple items exist,
    // preserve them by wrapping in a child AND group.
    if (serializedItems.length === 0) {
      return { not: { and: [] } };
    }
    if (serializedItems.length === 1) {
      return { not: serializedItems[0]! };
    }
    return { not: { and: serializedItems } };
  } else {
    return { or: serializedItems };
  }
}

/**
 * Serialize condition to JSON string
 */
function serializeCondition(group: ConditionGroup): string {
  // Serialize the group directly without wrapper
  const serialized = serializeGroup(group);
  return JSON.stringify(serialized, null, 2);
}

// ---------------------------------------------------------------------------
// Old-format expression serialization
//
// `isOldFormat` fields (Until / Query `where`) serialize as a JavaScript
// expression wrapped in `${...}`, e.g.
//   ${triggerOutputs()?.['x'] == 'foo'}
//   ${x.includes('foo')}
// The body must be valid JS — the runtime evaluates `${...}` as JavaScript,
// so Logic Apps function names like `equals()` / `contains()` are *not*
// valid here. The parser still accepts the legacy `@equals(...)` template
// form for round-tripping older saved workflows.
//
// Users with logic the visual builder can't represent (e.g.
// `${item()?.status === 200 || item()?.status === '200'}`) toggle into a
// free-form custom-expression editor; that text is round-tripped verbatim.
// ---------------------------------------------------------------------------

/** Coerce a field value to its string form (mirrors what gets serialized). */
function coerceConditionValueToString(value: string | ValueSegment[]): string {
  return isValueSegmentArray(value) ? segmentsToString(value) : value || '';
}

/**
 * Find the index of the `)` matching the `(` at `openIdx`, or -1.
 * Skips characters inside single-quoted strings (with `''` as escape) so
 * literals like `'a)b'` don't unbalance the count.
 */
function findMatchingClose(s: string, openIdx: number): number {
  let depth = 0;
  let inQuote = false;
  for (let i = openIdx; i < s.length; i++) {
    const ch = s[i];
    if (inQuote) {
      if (ch === "'") {
        if (s[i + 1] === "'") i++;
        else inQuote = false;
      }
      continue;
    }
    if (ch === "'") { inQuote = true; continue; }
    if (ch === '(') depth++;
    else if (ch === ')' && --depth === 0) return i;
  }
  return -1;
}

/** True if `value` is exactly one balanced `${...}` block from start to end. */
function isSingleExpressionBlock(value: string): boolean {
  if (!value.startsWith('${') || !value.endsWith('}')) return false;
  let depth = 0;
  let inQuote = false;
  for (let i = 0; i < value.length; i++) {
    const ch = value[i];
    if (inQuote) {
      if (ch === "'") {
        if (value[i + 1] === "'") i++;
        else inQuote = false;
      }
      continue;
    }
    if (ch === "'") { inQuote = true; continue; }
    if (ch === '$' && value[i + 1] === '{') { depth++; i++; }
    else if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0 && i < value.length - 1) return false;
    }
  }
  return depth === 0;
}

/** Strip a `${...}` or `@` wrapper; returns the inner expression or null. */
function unwrapOldFormatExpression(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.startsWith('${') && trimmed.endsWith('}')) {
    return isSingleExpressionBlock(trimmed) ? trimmed.slice(2, -1).trim() : null;
  }
  if (trimmed.startsWith('@')) return trimmed.slice(1).trim();
  return null;
}

/**
 * The `${...}` wrapper is evaluated as JavaScript by the runtime, so its
 * body must be JS-syntactically valid. We emit JS-infix comparisons
 * (`a == b`, `!x`, `&&`/`||`) and method calls (`x.includes(y)`) rather
 * than Logic Apps expression functions. The parser below accepts both
 * the new JS shape (current emit) and the legacy `equals(a, b)` function
 * form (for older saved workflows).
 *
 * Optional access uses `?.[...]`, not `?[...]` — `?[...]` is not valid
 * JS optional-chaining.
 */
const JS_INFIX_OPERATOR_MAP: Record<ConditionOperator, string> = {
  equals: '==',
  notEquals: '!=',
  greaterThan: '>',
  lessThan: '<',
  greaterOrEquals: '>=',
  lessOrEquals: '<=',
  // Non-infix; handled specially below
  contains: '',
  notContains: '',
  startsWith: '',
  endsWith: '',
  isEmpty: '',
  isNotEmpty: '',
};

/**
 * Walk `expr` quote-aware and insert `.` between any `?` and following `[`
 * outside a string literal. Handles both `''` (template-engine) and `\'`
 * (JS) escape forms inside single-quoted strings.
 */
function ensureOptionalChainDots(expr: string): string {
  let out = '';
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i]!;
    if (inSingle) {
      out += ch;
      if (ch === '\\' && i + 1 < expr.length) { out += expr[i + 1]!; i++; continue; }
      if (ch === "'") {
        if (expr[i + 1] === "'") { out += expr[i + 1]!; i++; continue; }
        inSingle = false;
      }
      continue;
    }
    if (inDouble) {
      out += ch;
      if (ch === '\\' && i + 1 < expr.length) { out += expr[i + 1]!; i++; continue; }
      if (ch === '"') inDouble = false;
      continue;
    }
    if (ch === "'") { inSingle = true; out += ch; continue; }
    if (ch === '"') { inDouble = true; out += ch; continue; }
    if (ch === '?' && expr[i + 1] === '[') { out += '?.'; continue; }
    out += ch;
  }
  return out;
}

/**
 * Format an operand for splicing into a JS expression. Token references
 * become bare expressions (with `?[` normalised to `?.[`); literal strings
 * are single-quoted with `\'` and `\\` JS escapes.
 */
function formatOldFormatOperand(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "''";
  const unwrapped = unwrapOldFormatExpression(trimmed);
  if (unwrapped !== null) return ensureOptionalChainDots(unwrapped);
  if (!isNaN(Number(trimmed))) return trimmed;
  if (trimmed === 'true' || trimmed === 'false' || trimmed === 'null') return trimmed;
  return `'${trimmed.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

function serializeRowOldFormat(row: ConditionRow): string {
  const op1 = formatOldFormatOperand(segmentsToString(row.operand1));
  const op2 = formatOldFormatOperand(segmentsToString(row.operand2));
  // Receivers are parenthesised before string-method calls so complex
  // operands (e.g. `a + b`, conditionals, numeric literals like `42`)
  // bind correctly. Without parens, `a + b.includes(...)` parses as
  // `a + (b.includes(...))`, and `42.includes(...)` is a JS syntax error
  // because `42.` is read as a float literal.
  switch (row.operator) {
    case 'isEmpty':
      return `(${op1} == null || ${op1} == '')`;
    case 'isNotEmpty':
      return `(${op1} != null && ${op1} != '')`;
    case 'contains':
      return `(${op1}).includes(${op2})`;
    case 'notContains':
      return `!(${op1}).includes(${op2})`;
    case 'startsWith':
      return `(${op1}).startsWith(${op2})`;
    case 'endsWith':
      return `(${op1}).endsWith(${op2})`;
    default: {
      const infix = JS_INFIX_OPERATOR_MAP[row.operator];
      return `${op1} ${infix} ${op2}`;
    }
  }
}

function serializeGroupOldFormat(group: ConditionGroup): string {
  const items = group.items.map((item) =>
    item.type === 'row' ? serializeRowOldFormat(item) : serializeGroupOldFormat(item)
  );
  if (items.length === 0) return '';
  if (group.operator === 'not') {
    if (items.length === 1) return `!(${items[0]})`;
    return `!(${items.join(' && ')})`;
  }
  if (items.length === 1) return items[0]!;
  const sep = group.operator === 'and' ? ' && ' : ' || ';
  return `(${items.join(sep)})`;
}

/** Serialize as `${triggerBody()?.['x'] == 'foo'}`. */
function serializeConditionOldFormat(group: ConditionGroup): string {
  const expr = serializeGroupOldFormat(group);
  return expr ? `\${${expr}}` : '';
}

/**
 * True when `expr` is either the JS-infix form (current serialization
 * output) OR exactly one balanced call to a known legacy function
 * (and/or/not, or an operator in REVERSE_OLD_FORMAT_MAP), recursively for
 * and/or/not arguments. Rejects anything with extra content after the
 * outermost call (e.g. `equals(a,b) || foo`).
 */
function isWellFormedKnownCall(expr: string): boolean {
  const s = expr.trim();
  if (parseJsExpr(s) !== null) return true;
  const open = s.indexOf('(');
  if (open === -1) return false;
  const close = findMatchingClose(s, open);
  if (close !== s.length - 1) return false;
  const funcName = s.slice(0, open).trim();
  const inner = s.slice(open + 1, close).trim();
  if (funcName === 'and' || funcName === 'or') {
    const args = splitFunctionArgs(inner);
    return args.length > 0 && args.every(isWellFormedKnownCall);
  }
  if (funcName === 'not') return isWellFormedKnownCall(inner);
  return funcName in REVERSE_OLD_FORMAT_MAP;
}

/**
 * Quoted-string contents that the JS parser would silently coerce to a
 * bare primitive on round-trip (e.g. `'200'` becomes `200`, `'true'`
 * becomes `true`). Detected via {@link isCoercibleStringLiteral}.
 */
const COERCIBLE_KEYWORDS = new Set(['true', 'false', 'null']);

function isCoercibleStringLiteral(content: string): boolean {
  const trimmed = content.trim();
  return trimmed !== ''
    && (COERCIBLE_KEYWORDS.has(trimmed) || !isNaN(Number(trimmed)));
}

/**
 * Quote-aware scan for JS constructs the visual builder can't round-trip
 * losslessly. Both cases collapse silently on serialize and would change
 * the saved expression's meaning, so we force custom mode instead.
 *
 * - Strict equality (`===` / `!==`) folds to `==` / `!=`.
 * - Quoted numeric / `true` / `false` / `null` literals (e.g. `'200'`)
 *   are unquoted during parse and re-emitted as the bare primitive,
 *   collapsing the string-vs-primitive distinction.
 */
function hasLossyJsConstruct(expr: string): boolean {
  let i = 0;
  while (i < expr.length) {
    const ch = expr[i]!;
    if (ch === "'" || ch === '"') {
      const quote = ch;
      const start = ++i;
      while (i < expr.length) {
        const c = expr[i]!;
        if (c === '\\' && i + 1 < expr.length) { i += 2; continue; }
        if (quote === "'" && c === "'" && expr[i + 1] === "'") { i += 2; continue; }
        if (c === quote) break;
        i++;
      }
      if (isCoercibleStringLiteral(expr.slice(start, i))) return true;
      i++; // skip closing quote
      continue;
    }
    if ((ch === '=' || ch === '!') && expr[i + 1] === '=' && expr[i + 2] === '=') {
      return true;
    }
    i++;
  }
  return false;
}

/**
 * True when an old-format value is a recognised expression the visual
 * builder can fully represent — JS-infix (`${a == b}`) or the legacy
 * Logic Apps template form (`@equals(a, b)`) from older saved workflows.
 * Empty value also returns true (starter row). Free-form JS like
 * `${item()?.status === 200 || ...}` returns false → custom mode.
 *
 * Lossy constructs (see `hasLossyJsConstruct`) also force custom mode even
 * if the parser would otherwise accept the input.
 */
export function isStructuredOldFormatExpression(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  const inner = unwrapOldFormatExpression(trimmed);
  if (inner === null) return false;
  if (hasLossyJsConstruct(inner)) return false;
  return isWellFormedKnownCall(inner);
}

/**
 * Check if a condition has any meaningful content
 */
function hasConditionContent(group: ConditionGroup): boolean {
  if (group.items.length === 0) return false;
  return group.items.some((item) => {
    if (item.type === 'row') {
      const op1 = segmentsToString(item.operand1).trim();
      const op2 = segmentsToString(item.operand2).trim();
      return op1 !== '' || op2 !== '';
    }
    return hasConditionContent(item);
  });
}

/**
 * Single comparison row: operand1 <operator> operand2 [delete?].
 * Used by both the full builder (with delete) and old-format mode (without).
 * When `onDelete` is omitted, the delete column is hidden and a slimmer grid is used.
 */
interface ComparisonRowProps {
  row: ConditionRow;
  namePrefix: string;
  availableOperators: ConditionOperator[];
  disabled: boolean;
  showAgentParameterButton?: boolean | undefined;
  agentParameterSchema?: AgentParameterSchema | undefined;
  actionOutputs?: Record<string, unknown> | undefined;
  onOperand1Change: (value: ValueSegment[]) => void;
  onOperatorChange: (operator: ConditionOperator) => void;
  onOperand2Change: (value: ValueSegment[]) => void;
  /** When provided, a delete button is rendered. Omit for old-format / single-row mode. */
  onDelete?: (() => void) | undefined;
}

const ComparisonRow = ({
  row,
  namePrefix,
  availableOperators,
  disabled,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  onOperand1Change,
  onOperatorChange,
  onOperand2Change,
  onDelete,
}: ComparisonRowProps) => {
  const styles = useStyles();
  const isUnary = UNARY_OPERATORS.includes(row.operator);
  const showDelete = typeof onDelete === 'function';

  let rowClass: string;
  if (showDelete) {
    rowClass = isUnary ? styles.rowUnary : styles.row;
  } else {
    rowClass = isUnary ? styles.simpleRowUnary : styles.simpleRow;
  }

  return (
    <div className={rowClass}>
      <div className={styles.operandInput}>
        <TokenizedInput
          name={`${namePrefix}_operand1`}
          value={row.operand1}
          onChange={onOperand1Change}
          placeholder="Value 1"
          disabled={disabled}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />
      </div>
      <Dropdown
        className={styles.comparisonSelector}
        size="small"
        value={OPERATOR_LABELS[row.operator]}
        selectedOptions={[row.operator]}
        onOptionSelect={(_e, data) =>
          onOperatorChange(data.optionValue as ConditionOperator)
        }
        disabled={disabled}
        aria-label="Comparison operator"
      >
        {availableOperators.map((op) => (
          <Option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </Option>
        ))}
      </Dropdown>
      {!isUnary && (
        <div className={styles.operandInput}>
          <TokenizedInput
            name={`${namePrefix}_operand2`}
            value={row.operand2}
            onChange={onOperand2Change}
            placeholder="Value 2"
            disabled={disabled}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={actionOutputs}
          />
        </div>
      )}
      {showDelete && (
        <Tooltip content="Remove condition" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<DeleteFilled />}
            onClick={onDelete}
            disabled={disabled}
            className={styles.deleteButton}
            aria-label="Remove condition"
          />
        </Tooltip>
      )}
    </div>
  );
};

/**
 * Encapsulates the Visual/Custom mode toggle for `isOldFormat` fields.
 * Returns the mode flag, the custom-mode text buffer, and the handlers
 * that drive the toggle UI.
 */
type OldFormatCustomModeArgs = {
  isOldFormat: boolean;
  value: string | ValueSegment[];
  setCondition: (g: ConditionGroup) => void;
  onChange: (value: string | ValueSegment[]) => void;
  isInternalChangeRef: MutableRefObject<boolean>;
  lastSerializedRef: MutableRefObject<string>;
};

function useOldFormatCustomMode({
  isOldFormat,
  value,
  setCondition,
  onChange,
  isInternalChangeRef,
  lastSerializedRef,
}: OldFormatCustomModeArgs) {
  const valueAsString = useMemo(() => coerceConditionValueToString(value), [value]);

  const [isCustomMode, setIsCustomMode] = useState<boolean>(
    () => isOldFormat && valueAsString.trim() !== ''
      && !isStructuredOldFormatExpression(valueAsString)
  );

  const [customText, setCustomText] = useState<string>(
    () => (isOldFormat ? valueAsString : '')
  );

  const lastSentRef = useRef<string>(valueAsString);

  // Sync the buffer when the upstream value changes from elsewhere.
  useEffect(() => {
    if (!isCustomMode) return;
    if (valueAsString !== lastSentRef.current) {
      setCustomText(valueAsString);
      lastSentRef.current = valueAsString;
    }
  }, [valueAsString, isCustomMode]);

  const emitRaw = useCallback((raw: string) => {
    lastSentRef.current = raw;
    isInternalChangeRef.current = true;
    onChange([{ id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: raw }]);
  }, [isInternalChangeRef, onChange]);

  const canSwitchToVisual = useMemo(
    () => isStructuredOldFormatExpression(customText),
    [customText]
  );

  const switchToVisual = useCallback(() => {
    const trimmed = customText.trim();
    if (trimmed === '') {
      // Reset condition; otherwise visual would render the stale parsed row.
      const empty = createEmptyGroup();
      setCondition(empty);
      lastSerializedRef.current = '';
      setIsCustomMode(false);
      return;
    }
    if (!isStructuredOldFormatExpression(trimmed)) return;
    const parsed = parseOldFormatCondition(trimmed);
    const serialized = serializeConditionOldFormat(parsed);
    setCondition(parsed);
    lastSerializedRef.current = serialized;
    emitRaw(serialized);
    setIsCustomMode(false);
  }, [customText, setCondition, lastSerializedRef, emitRaw]);

  const switchToCustom = useCallback(() => {
    // Seed from value, not condition: the starter row would serialize to `${'' == ''}`.
    setCustomText(valueAsString);
    setIsCustomMode(true);
  }, [valueAsString]);

  const handleCustomChange = useCallback((segments: ValueSegment[]) => {
    const newValue = segmentsToString(segments);
    setCustomText(newValue);
    emitRaw(newValue);
  }, [emitRaw]);

  return {
    valueAsString,
    isCustomMode,
    customText,
    canSwitchToVisual,
    switchToVisual,
    switchToCustom,
    handleCustomChange,
  };
}

export const ConditionField = ({
  name,
  label,
  value,
  onChange,
  description,
  required,
  disabled = false,
  error,
  operators,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
  isOldFormat,
  endActions,
}: ConditionFieldProps) => {
  const styles = useStyles();

  // Check if the value is a raw workflow expression (e.g., @greater(x, 0)).
  // Filter/Query 'where' clauses are bare expressions, not structured condition JSON.
  const expressionString = useMemo(
    () => getConditionExpressionString(value),
    [value]
  );

  // Track expanded vs collapsed mode
  const [isExpanded, setIsExpanded] = useState(true);

  // Track focus state for showing preview
  const { isFocused, containerHandlers, previewHandlers } = useFieldPreviewFocus();

  // Internal condition state - maintains stable IDs across edits
  const [condition, setCondition] = useState<ConditionGroup>(() => parseCondition(value));

  // Track if we initiated the change (to avoid re-parsing our own updates)
  const isInternalChangeRef = useRef(false);

  // Track the last serialized value we sent out
  const lastSerializedRef = useRef<string>(serializeCondition(condition));

  // Sync from external value changes only
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    const stringValue = isValueSegmentArray(value)
      ? segmentsToString(value)
      : value;

    // Only re-parse if the external value is different from what we last sent
    if (stringValue !== lastSerializedRef.current) {
      const parsed = parseCondition(value);
      setCondition(parsed);
      lastSerializedRef.current = stringValue || '{}';
    }
  }, [value]);

  // Available operators
  const availableOperators = operators || (Object.keys(OPERATOR_LABELS) as ConditionOperator[]);

  // Raw JSON text for collapsed mode
  const rawJsonValue = useMemo(() => {
    const stringValue = isValueSegmentArray(value)
      ? segmentsToString(value)
      : value;
    return stringValue || '{}';
  }, [value]);

  // Get the serialized expression for preview
  const serializedExpression = useMemo(() => {
    try {
      const stringValue = isValueSegmentArray(value)
        ? segmentsToString(value)
        : value;
      if (stringValue) {
        return JSON.parse(stringValue);
      }
    } catch {
      // Parse error
    }
    return null;
  }, [value]);

  // Update condition and notify onChange
  const updateCondition = useCallback((newCondition: ConditionGroup) => {
    const serialized = isOldFormat
      ? serializeConditionOldFormat(newCondition)
      : serializeCondition(newCondition);
    lastSerializedRef.current = serialized;
    isInternalChangeRef.current = true;
    setCondition(newCondition);
    onChange([{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: serialized,
    }]);
  }, [onChange, isOldFormat]);

  // Update an item in the tree
  const updateItem = useCallback((
    group: ConditionGroup,
    itemId: string,
    updater: (item: ConditionItem) => ConditionItem
  ): ConditionGroup => {
    // Check if this group is the target
    if (group.id === itemId) {
      return updater(group) as ConditionGroup;
    }

    return {
      ...group,
      items: group.items.map((item) => {
        if (item.id === itemId) {
          return updater(item);
        }
        if (item.type === 'group') {
          return updateItem(item, itemId, updater);
        }
        return item;
      }),
    };
  }, []);

  // Remove an item from the tree
  const removeItem = useCallback((
    group: ConditionGroup,
    itemId: string
  ): ConditionGroup => {
    const filtered = group.items.filter((item) => item.id !== itemId);
    return {
      ...group,
      items: filtered.map((item) =>
        item.type === 'group' ? removeItem(item, itemId) : item
      ),
    };
  }, []);

  // Add item to a group
  const addToGroup = useCallback((
    group: ConditionGroup,
    groupId: string,
    newItem: ConditionItem
  ): ConditionGroup => {
    if (group.id === groupId) {
      return {
        ...group,
        items: [...group.items, newItem],
      };
    }
    return {
      ...group,
      items: group.items.map((item) =>
        item.type === 'group' ? addToGroup(item, groupId, newItem) : item
      ),
    };
  }, []);

  // Handle group operator change
  const handleGroupOperatorChange = useCallback((groupId: string, op: 'and' | 'or' | 'not') => {
    const newCondition = updateItem(condition, groupId, (item) =>
      item.type === 'group' ? { ...item, operator: op } : item
    );
    updateCondition(newCondition);
  }, [condition, updateItem, updateCondition]);

  // Handle row operand1 change
  const handleOperand1Change = useCallback((rowId: string, newValue: ValueSegment[]) => {
    const newCondition = updateItem(condition, rowId, (item) =>
      item.type === 'row' ? { ...item, operand1: newValue } : item
    );
    updateCondition(newCondition);
  }, [condition, updateItem, updateCondition]);

  // Handle row operator change
  const handleRowOperatorChange = useCallback((rowId: string, op: ConditionOperator) => {
    const newCondition = updateItem(condition, rowId, (item) =>
      item.type === 'row' ? { ...item, operator: op } : item
    );
    updateCondition(newCondition);
  }, [condition, updateItem, updateCondition]);

  // Handle row operand2 change
  const handleOperand2Change = useCallback((rowId: string, newValue: ValueSegment[]) => {
    const newCondition = updateItem(condition, rowId, (item) =>
      item.type === 'row' ? { ...item, operand2: newValue } : item
    );
    updateCondition(newCondition);
  }, [condition, updateItem, updateCondition]);

  // Handle remove item
  const handleRemoveItem = useCallback((itemId: string) => {
    const newCondition = removeItem(condition, itemId);
    updateCondition(newCondition);
  }, [condition, removeItem, updateCondition]);

  // Handle add condition row
  const handleAddCondition = useCallback((groupId: string) => {
    const newCondition = addToGroup(condition, groupId, createConditionRow());
    updateCondition(newCondition);
  }, [condition, addToGroup, updateCondition]);

  // Handle add nested group
  const handleAddGroup = useCallback((groupId: string) => {
    const newCondition = addToGroup(condition, groupId, createNestedGroup());
    updateCondition(newCondition);
  }, [condition, addToGroup, updateCondition]);

  // ---- Old-format (simple) mode helpers ----
  // In old format the condition is a single comparison: operand1 <op> operand2.
  // No AND/OR groups, no add condition / add group, no delete row.
  const oldFormatRow = useMemo<ConditionRow>(() => {
    const firstRow = condition.items.find(
      (item): item is ConditionRow => item.type === 'row'
    );
    return firstRow ?? createConditionRow();
  }, [condition]);

  const updateOldFormatRow = useCallback(
    (updated: ConditionRow) => {
      updateCondition({
        id: condition.id,
        type: 'group',
        operator: 'and',
        items: [updated],
      });
    },
    [condition.id, updateCondition]
  );

  // ---- Old-format custom-expression mode ----
  // The simple builder is single-row only. Users with multi-clause logic
  // (`${a || b}`) or anything else the builder can't represent toggle into
  // custom mode — a free-form TokenizedInput that round-trips verbatim.
  const {
    valueAsString,
    isCustomMode: oldFormatCustomMode,
    customText: customExpressionValue,
    canSwitchToVisual: canParseCustomAsVisual,
    switchToVisual: switchOldFormatToVisual,
    switchToCustom: switchOldFormatToCustom,
    handleCustomChange: handleCustomExpressionChange,
  } = useOldFormatCustomMode({
    isOldFormat: !!isOldFormat,
    value,
    setCondition,
    onChange,
    isInternalChangeRef,
    lastSerializedRef,
  });

  // Handle raw JSON change in collapsed mode
  const handleRawChange = useCallback((segments: ValueSegment[]) => {
    onChange(segments);
  }, [onChange]);

  // Convert raw JSON to segments for TokenizedInput
  const rawJsonSegments: ValueSegment[] = useMemo(() => [{
    id: generateSegmentId(),
    type: ValueSegmentType.LITERAL,
    value: rawJsonValue,
  }], [rawJsonValue]);

  // Render a condition row
  const renderRow = (row: ConditionRow) => (
    <ComparisonRow
      key={row.id}
      row={row}
      namePrefix={`${name}_${row.id}`}
      availableOperators={availableOperators}
      disabled={disabled}
      showAgentParameterButton={showAgentParameterButton}
      agentParameterSchema={agentParameterSchema}
      actionOutputs={actionOutputs}
      onOperand1Change={(v) => handleOperand1Change(row.id, v)}
      onOperatorChange={(op) => handleRowOperatorChange(row.id, op)}
      onOperand2Change={(v) => handleOperand2Change(row.id, v)}
      onDelete={() => handleRemoveItem(row.id)}
    />
  );

  // Render a condition group
  const renderGroup = (group: ConditionGroup, isRoot = false) => (
    <div key={group.id} className={isRoot ? styles.rootGroup : styles.nestedGroup}>
      <div className={styles.groupHeader}>
        <Dropdown
          className={styles.operatorSelector}
          size="small"
          value={group.operator.toUpperCase()}
          selectedOptions={[group.operator]}
          onOptionSelect={(_e, data) =>
            handleGroupOperatorChange(group.id, data.optionValue as 'and' | 'or' | 'not')
          }
          disabled={disabled}
          aria-label="Group operator"
        >
          <Option value="and">AND</Option>
          <Option value="or">OR</Option>
          <Option value="not">NOT</Option>
        </Dropdown>
        {!isRoot && (
          <Tooltip content="Remove group" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<DeleteFilled />}
              onClick={() => handleRemoveItem(group.id)}
              disabled={disabled}
              className={styles.deleteButton}
              aria-label="Remove group"
            />
          </Tooltip>
        )}
      </div>

      {group.items.length === 0 ? (
        <div className={styles.emptyState}>
          No conditions. Add a condition or group below.
        </div>
      ) : (
        group.items.map((item) =>
          item.type === 'row' ? renderRow(item) : renderGroup(item)
        )
      )}

      <div className={styles.addButtons}>
        <Button
          appearance="subtle"
          size="small"
          icon={<AddFilled />}
          onClick={() => handleAddCondition(group.id)}
          disabled={disabled}
        >
          Add Condition
        </Button>
        <Button
          appearance="subtle"
          size="small"
          icon={<BranchForkFilled />}
          onClick={() => handleAddGroup(group.id)}
          disabled={disabled}
        >
          Add Group
        </Button>
      </div>
    </div>
  );

  // Shared label header for all three render branches (old-format,
  // raw-expression fallback, full visual builder). Only the full
  // builder needs the visual / JSON mode toggle, so it renders its
  // own labelRow inline and uses `endActions` differently.
  const labelHeader = (
    <div className={styles.labelRow}>
      <Label className={styles.label} htmlFor={name}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </Label>
      {endActions && <EndActionsSlot>{endActions}</EndActionsSlot>}
    </div>
  );

  // Old-format mode (Until / Query): a single simple comparison row, with a
  // toggle to switch into custom-expression mode for arbitrary JavaScript
  // (e.g. `${item()?.status === 200 || item()?.status === '200'}`).
  if (isOldFormat) {
    const row = oldFormatRow;
    const oldFormatHeader = (
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <div className={styles.modeToggle}>
          {endActions && <EndActionsSlot>{endActions}</EndActionsSlot>}
          <Tooltip
            content={
              canParseCustomAsVisual
                ? 'Visual editor'
                : "Visual editor can't represent this expression"
            }
            relationship="label"
          >
            <Button
              appearance={!oldFormatCustomMode ? 'primary' : 'subtle'}
              size="small"
              icon={<TextFieldFilled />}
              onClick={switchOldFormatToVisual}
              disabled={disabled || (oldFormatCustomMode && !canParseCustomAsVisual)}
              aria-label="Visual editor"
              aria-pressed={!oldFormatCustomMode}
            />
          </Tooltip>
          <Tooltip content="Edit as expression" relationship="label">
            <Button
              appearance={oldFormatCustomMode ? 'primary' : 'subtle'}
              size="small"
              icon={<CodeFilled />}
              onClick={switchOldFormatToCustom}
              disabled={disabled}
              aria-label="Edit as expression"
              aria-pressed={oldFormatCustomMode}
            />
          </Tooltip>
        </div>
      </div>
    );

    return (
      <div className={styles.container}>
        {oldFormatHeader}
        {description && <Text className={styles.description}>{description}</Text>}
        <div className={styles.editorWrapper} {...containerHandlers}>
          {oldFormatCustomMode ? (
            <TokenizedInput
              name={name}
              value={[{
                id: 'expr',
                type: ValueSegmentType.LITERAL,
                value: customExpressionValue,
              }]}
              onChange={handleCustomExpressionChange}
              placeholder="${item()?.status === 200}"
              disabled={disabled}
              actionOutputs={actionOutputs}
            />
          ) : (
            <ComparisonRow
              row={row}
              namePrefix={name}
              availableOperators={availableOperators}
              disabled={disabled}
              showAgentParameterButton={showAgentParameterButton}
              agentParameterSchema={agentParameterSchema}
              actionOutputs={actionOutputs}
              onOperand1Change={(v) => updateOldFormatRow({ ...row, operand1: v })}
              onOperatorChange={(op) => updateOldFormatRow({ ...row, operator: op })}
              onOperand2Change={(v) => updateOldFormatRow({ ...row, operand2: v })}
            />
          )}
          {isFocused && valueAsString.trim() && (
            <ExpressionPreview
              value={valueAsString}
              actionOutputs={actionOutputs}
              hasRunData={hasRunData}
              isStale={isStale}
              mode="overlay"
              {...previewHandlers}
            />
          )}
        </div>
        {error && (
          <Text className={styles.error} role="alert">{error}</Text>
        )}
      </div>
    );
  }

  // When the value is a raw expression (not structured condition JSON), render a tokenized input.
  // Skip this for old-format (Until/Query) — those are parsed into condition groups above.
  if (expressionString !== null && !isOldFormat) {
    return (
      <div className={styles.container}>
        {labelHeader}
        {description && <Text className={styles.description}>{description}</Text>}
        <TokenizedInput
          name={name}
          value={[{
            id: 'expr',
            type: ValueSegmentType.LITERAL,
            value: expressionString,
          }]}
          onChange={(segments) => {
            const newValue = segmentsToString(segments);
            onChange(newValue);
          }}
          placeholder="Enter condition expression..."
          disabled={disabled}
          actionOutputs={actionOutputs}
        />
        {error && (
          <Text className={styles.error} role="alert">{error}</Text>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <div className={styles.modeToggle}>
          {endActions}
          <Tooltip content="Visual editor" relationship="label">
            <Button
              appearance={isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<TextFieldFilled />}
              onClick={() => setIsExpanded(true)}
              disabled={disabled}
              aria-label="Visual editor"
            />
          </Tooltip>
          <Tooltip content="Edit as JSON" relationship="label">
            <Button
              appearance={!isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<CodeFilled />}
              onClick={() => setIsExpanded(false)}
              disabled={disabled}
              aria-label="Edit as JSON"
            />
          </Tooltip>
        </div>
      </div>

      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      {isExpanded ? (
        // Expanded mode - visual condition builder
        <div className={styles.editorWrapper} {...containerHandlers}>
          {renderGroup(condition, true)}
          {/* Expression preview overlay */}
          {isFocused && hasConditionContent(condition) && serializedExpression && (
            <ExpressionPreview
              value={serializedExpression}
              actionOutputs={actionOutputs}
              hasRunData={hasRunData}
              isStale={isStale}
              mode="overlay"
              {...previewHandlers}
            />
          )}
        </div>
      ) : (
        // Collapsed mode - raw JSON editor
        <TokenizedInput
          name={name}
          value={rawJsonSegments}
          onChange={handleRawChange}
          placeholder="{}"
          multiline
          disabled={disabled}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />
      )}

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

ConditionField.displayName = 'ConditionField';
